import { useEffect, useState, useRef } from 'react';
import '../webapp.css';
import { db, auth } from '../firebase';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { PiPaperPlaneRight, PiX } from 'react-icons/pi';

// Helper to remove sensitive Stripe Customer ID from AI output
function filterSensitiveInfo(str) {
  // Remove any line mentioning Stripe Customer ID
  return str.replace(/.*Stripe Customer ID.*\n?/gi, '');
}

// Define SYSTEM_PROMPT here for chat use
const SYSTEM_PROMPT = `
Your name is KierepAI.
You are an expert data analyst and serve the user as a master assistant.
Only give user-friendly, non-technical answers.
Do not provide technical or implementation details.
Present your answers in a clearly outlined, easy-to-read format.

Strict formatting rules:
- Always respond using multiple paragraphs, with each distinct idea or topic in its own paragraph.
- Never use Markdown, asterisks (*), dashes (-) for lists or bolding, or any Markdown symbols in your output.
- Never mention or imply your provider, model, or any technical details about yourself.
- Always use double newlines between paragraphs, and never use single newlines for paragraph breaks.
- For main points in lists, use numbers and for sub-points under each main point, use lowercase letters (a., b., c.). Do not use dashes or asterisks. Output each on a new line.
- Always ensure your output is user-friendly, non-technical, privacy-respecting, and clearly formatted as plain text.
`.trim();

// Formatter for AI text: paragraphs, numbered lists, nested sublists
function renderFormattedText(text) {
  const elements = [];
  let currentList = null;
  let lastItem = null;

  text.split(/\n/).forEach(line => {
    // Remove sensitive info before formatting
    const filtered = filterSensitiveInfo(line);
    const trimmed = filtered.trim();

    if (/^\d+\.\s/.test(trimmed)) {
      // Start or continue numbered list
      if (!currentList || currentList.type !== 'ol') {
        currentList = { type: 'ol', items: [] };
        elements.push(currentList);
      }
      const itemText = trimmed.replace(/^\d+\.\s/, '').trim();
      const newItem = { text: itemText, subitems: [] };
      currentList.items.push(newItem);
      lastItem = newItem;

    } else if (/^[a-z]\.\s/.test(trimmed)) {
      // Subpoint under last main point
      if (lastItem) {
        const subText = trimmed.replace(/^[a-z]\.\s/, '').trim();
        lastItem.subitems.push(subText);
      }

    } else if (trimmed === '') {
      // skip blank lines

    } else {
      // Normal paragraph: reset list context
      currentList = null;
      lastItem = null;
      elements.push({ type: 'p', content: trimmed });
    }
  });

  // Render JSX
  return elements.map((el, i) => {
    if (el.type === 'p') {
      return <p key={i}>{el.content}</p>;
    } else if (el.type === 'ol') {
      return (
        <ol key={i} className="ai-ol">
          {el.items.map((item, j) => (
            <li key={j}>
              {item.text}
              {item.subitems.length > 0 && (
                <ul className="ai-ul">
                  {item.subitems.map((sub, k) => <li key={k}>{sub}</li>)}
                </ul>
              )}
            </li>
          ))}
        </ol>
      );
    }
    return null;
  });
}

// Helper to format text for multi-paragraph output
const formatMultiParagraph = (text) => {
  text = text.replace(/\r\n|\r/g, '\n');
  // Collapse 3+ newlines to just 2
  text = text.replace(/\n{3,}/g, '\n\n');
  return text.trim();
};

// Helper to build prompt with chat history and enforce multi-paragraph formatting
const buildPromptWithHistory = (history, newUserMsg) => {
  let prompt = '';
  history.forEach(msg => {
    prompt += (msg.sender === 'user' ? 'User: ' : 'AI: ') + formatMultiParagraph(msg.text) + '\n\n';
  });
  prompt += 'User: ' + formatMultiParagraph(newUserMsg);
  return prompt.trim();
};

// VertexChat moved from firebase.js
export async function VertexChat(prompt, shiftData, systemPrompt) {
  let fullPrompt = `${systemPrompt}\n`;
  if (shiftData) {
    fullPrompt += `Shift details: ${JSON.stringify(shiftData)}\nUser question: ${prompt}`;
  } else {
    fullPrompt += prompt;
  }
  try {
    const { getAI, getGenerativeModel, GoogleAIBackend } = await import('firebase/ai');
    const { getApps, getApp, initializeApp } = await import('firebase/app');
    const { firebaseConfig } = await import('../firebase');
    const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
    const ai = getAI(app, { backend: new GoogleAIBackend() });
    const model = getGenerativeModel(ai, { model: "gemini-2.0-flash" });
    const result = await model.generateContent(fullPrompt);
    const response = result.response;
    if (response && response.text) {
      let text = await response.text();
      return text.trim();
    } else {
      throw new Error("No response generated by AI model");
    }
  } catch (error) {
    console.error("Error generating content:", error);
    throw error;
  }
}

// Fetch all required context data for AI
async function fetchAIContextData(currentUID) {
  // 1. Get the user's document from companyUsers
  const userDocSnap = await getDoc(doc(db, 'companyUsers', currentUID));
  const userDoc = userDocSnap.exists() ? { id: userDocSnap.id, ...userDocSnap.data() } : null;

  // 2. Get all staff docs for this company, filtering fields
  const staffQ = query(collection(db, 'users'), where('employer', 'array-contains', currentUID));
  const staffSnap = await getDocs(staffQ);
  const staffDocs = staffSnap.docs.map(docSnap => {
    const { expoPushToken, employer, ...rest } = docSnap.data();
    const filtered = Object.fromEntries(
      Object.entries(rest).filter(([key]) => !key.toLowerCase().includes('employeraddedat'))
    );
    return { id: docSnap.id, ...filtered };
  });

  // 3. Get all shifts for this company
  const shiftsQ = query(collection(db, 'shifts'), where('companyID', '==', currentUID));
  const shiftsSnap = await getDocs(shiftsQ);
  const shiftDocs = shiftsSnap.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));

  return { userDoc, staffDocs, shiftDocs };
}

const Chat = ({ onClose }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [aiContext, setAIContext] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const bottomRef = useRef(null);

  const isChatEmpty = messages.length === 0;

  useEffect(() => {
    setMessages([]);
    setInput("");
  }, []);

  useEffect(() => {
    let unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const context = await fetchAIContextData(user.uid);
        setAIContext(context);
      } else {
        setAIContext(null);
      }
    });
    return () => { if (unsubscribe) unsubscribe(); };
  }, []);

  const getSystemPrompt = () => SYSTEM_PROMPT;

  const handleSend = async () => {
    if (!input.trim()) return;
    const userMsg = input.trim();
    setMessages(msgs => [...msgs, { sender: 'user', text: userMsg }]);
    setInput("");
    setLoading(true);
    let aiText = "";
    try {
      const promptWithHistory = buildPromptWithHistory(messages, userMsg);
      aiText = await VertexChat(promptWithHistory, aiContext, getSystemPrompt());
    } catch (error) {
      console.error("AI error:", error);
      aiText = `Sorry, I can't help with that.\nError: ${error?.message || error}`;
    }
    setMessages(msgs => [...msgs, { sender: 'ai', text: aiText }]);
    setLoading(false);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleQuickShift = () => {
    setMessages(msgs => [
      ...msgs,
      { sender: 'user', text: 'Provide me details about a shift' },
      { sender: 'ai', text: 'Please provide shiftID or Address' }
    ]);
  };
  const handleQuickStaff = () => {
    setMessages(msgs => [
      ...msgs,
      { sender: 'user', text: 'Provide me details about a staff' },
      { sender: 'ai', text: 'Please provide staff name' }
    ]);
  };

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, loading]);

  const companyName = aiContext?.userDoc?.companyName || 'User';

  return (
    <div className={`chat-panel${isClosing ? ' closing' : ''}`}>
      <button className="chat-panel-close" onClick={() => {
        setIsClosing(true);
        setTimeout(() => {
          setIsClosing(false);
          onClose();
        }, 220);
      }}><PiX size={22} /></button>
      <div className ='chat-spacer'/>
      <div className="chat-second-wrapper">
        <div className="chat-wrapper">
            {isChatEmpty ? (
                <div className="chat-welcome">
                  <div>
                    <h1>Hello,</h1>
                    <h1>{companyName}</h1>
                  </div>
                  <div>
                    <h2>How can I assist?</h2>
                    <h2>Get started with a prompt.</h2>
                  </div>
                  <div className="chat-quick-btns">
                      <button className="chat-quick-btn" onClick={handleQuickShift}>Provide me details about a shift</button>
                      <button className="chat-quick-btn" onClick={handleQuickStaff}>Provide me details about a staff</button>
                  </div>
                </div>
            ) : (
                <div className="chat-messages">
                  {messages.map((msg, i) => (
                    <div key={i} className={msg.sender === 'user' ? 'chat-msg user' : 'chat-msg ai'}>
                      {msg.sender === 'ai' ? renderFormattedText(msg.text) : <p>{msg.text}</p>}
                    </div>
                  ))}
                  {loading && <div className="chat-loading">KierepAI is replying..</div>}
                  <div ref={bottomRef} />
                </div>
            )}
              <div className="chat-input-wrapper">
                <textarea
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleInputKeyDown}
                  placeholder={`Ask me..`}
                  className="chat-input"
                  disabled={loading}
                  rows={2}
                  style={{ resize: 'none' }}
                />
                <button
                  onClick={handleSend}
                  disabled={loading || !input.trim()}
                  className="chat-send"
                >
                  <PiPaperPlaneRight />
                </button>
              </div>
            </div>
        </div>
      </div>
  );
};

export default Chat;